/**
 * useLabelTemplate Hook
 *
 * Hook to load the appropriate label template for printing.
 * Returns the default template for the given label type and canvas size.
 *
 * CRITICAL: This hook must be called in the print path to ensure
 * templates are loaded before rendering labels.
 *
 * @example
 * const { template, isLoading, error } = useLabelTemplate('POD');
 *
 * if (isLoading) return <Skeleton />;
 * if (!template) return <LegacyLabel />;
 *
 * return <TemplateBasedLabel template={template} />;
 */

import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import type {
  ComponentConfig,
  CanvasSize,
  LabelType,
} from '@/features/production-settings/components/label-layout/label-layout-types';
import {
  getLabelTypeFromFulfillment,
  findMatchingTemplate,
} from '../utils/label-renderer';

const STANDARD_SIZE: CanvasSize = { width: 102, height: 76, name: 'Standard (4x3")' };

// ============================================================================
// TYPES
// ============================================================================

export interface LabelTemplate {
  id: string;
  name: string;
  canvasSize: CanvasSize;
  components: ComponentConfig[];
  labelType: LabelType;
  isDefault: boolean;
  isBuiltIn: boolean;
}

// ============================================================================
// DEFAULT LABEL SIZE HOOK
// ============================================================================

/**
 * Load the effective default label size, respecting per-location overrides.
 *
 * When a location is selected, queries the location-scoped endpoint which
 * returns the location override if set, otherwise the org default.
 * When "All Locations" is selected (no location), uses the org-level default.
 *
 * Fallback chain: location override → org default → Standard 4x3" (102x76mm)
 *
 * @returns defaultLabelSize, loading state, error, and whether it's a location override
 */
export function useDefaultLabelSize() {
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id;

  // Location-scoped query (stations always have a location selected)
  const locationQuery = trpc.productionSettings.getLocationLabelSizeSettings.useQuery(
    { locationId: locationId! },
    { enabled: !!locationId }
  );

  // Org-level fallback when no location selected ("All Locations" mode)
  const orgQuery = trpc.productionSettings.getLabelSizeSettings.useQuery(
    undefined,
    { enabled: !locationId }
  );

  const defaultLabelSize: CanvasSize = locationId
    ? (locationQuery.data?.defaultLabelSize ?? STANDARD_SIZE)
    : (orgQuery.data?.defaultLabelSize ?? STANDARD_SIZE);

  return {
    defaultLabelSize,
    isLoading: locationId ? locationQuery.isLoading : orgQuery.isLoading,
    error: locationId ? locationQuery.error : orgQuery.error,
    isLocationOverride: locationQuery.data?.isLocationOverride ?? false,
  };
}

// ============================================================================
// PRIMARY HOOK
// ============================================================================

/**
 * Load the appropriate label template for printing.
 * Automatically selects based on fulfillment type and canvas size.
 *
 * If no preferredCanvasSize is provided, uses the organization's default
 * label size from production settings.
 *
 * @param fulfillmentType - 'POD' or 'P&P' (or variants)
 * @param preferredCanvasSize - Optional preferred canvas size (uses org default if not provided)
 * @returns template, loading state, error, and normalized label type
 */
export function useLabelTemplate(
  fulfillmentType: string,
  preferredCanvasSize?: CanvasSize
) {
  const labelType = getLabelTypeFromFulfillment(fulfillmentType);

  // Load the organization's default label size
  const { defaultLabelSize, isLoading: sizeLoading } = useDefaultLabelSize();

  // Use preferredCanvasSize if provided, otherwise use org default
  const canvasSize = preferredCanvasSize ?? defaultLabelSize;

  const { data: templates, isLoading: templatesLoading, error } = trpc.labelTemplates.list.useQuery();

  // Combined loading state
  const isLoading = sizeLoading || templatesLoading;

  // Find the best matching template
  const template = templates
    ? findMatchingTemplate(templates, labelType, canvasSize)
    : null;

  return {
    template: template as LabelTemplate | null,
    isLoading,
    error,
    labelType,
    /** The canvas size being used (from settings or override) */
    canvasSize,
  };
}

// ============================================================================
// BY ID HOOK
// ============================================================================

/**
 * Load a specific template by ID.
 * Use when you know which template to use (e.g., saved preference).
 *
 * @param templateId - Template UUID or null to skip
 * @returns template, loading state, and error
 */
export function useLabelTemplateById(templateId: string | null) {
  const { data: templates, isLoading, error } = trpc.labelTemplates.list.useQuery(
    undefined,
    { enabled: !!templateId }
  );

  const template = templateId
    ? templates?.find((t) => t.id === templateId) ?? null
    : null;

  return {
    template: template as LabelTemplate | null,
    isLoading,
    error,
  };
}

// ============================================================================
// DEFAULT FOR SIZE HOOK
// ============================================================================

/**
 * Load the default template for a specific canvas size.
 * Uses the getDefault endpoint which auto-seeds templates if needed.
 *
 * @param canvasSize - Canvas size to get default for
 * @returns template, loading state, and error
 */
export function useDefaultLabelTemplate(canvasSize: CanvasSize) {
  const { data: template, isLoading, error } = trpc.labelTemplates.getDefault.useQuery({
    canvasWidth: canvasSize.width,
    canvasHeight: canvasSize.height,
  });

  return {
    template: template as LabelTemplate | null,
    isLoading,
    error,
  };
}
